import React, { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Slider,
  Typography,
} from '@material-ui/core';
import { Close, RotateLeft, RotateRight } from '@material-ui/icons';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import axios_instance, { appURL } from '../../../services/auth/jwt/config';
import sweetAlert from '../../../f5/utils/sweetAlert';

interface AuxilioPhotoEditorProps {
  open: boolean;
  onClose: () => void;
  auxilioId: number;
  photoUrl: string;
  onSaveSuccess?: () => void;
}

const AuxilioPhotoEditor: React.FC<AuxilioPhotoEditorProps> = ({
  open,
  onClose,
  auxilioId,
  photoUrl,
  onSaveSuccess,
}) => {
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Carregar imagem via fetch para evitar problemas de CORS
  React.useEffect(() => {
    if (open && photoUrl) {
      const fullUrl = photoUrl.startsWith('http') ? photoUrl : `${appURL}${photoUrl}`;

      // Carregar imagem como blob e criar um elemento de imagem real
      fetch(fullUrl, { mode: 'cors' })
        .then(res => res.blob())
        .then(blob => {
          const url = URL.createObjectURL(blob);
          setImageUrl(url);

          // Criar elemento de imagem e aguardar o carregamento
          const img = new Image();
          img.onload = () => {
            setImageElement(img);
          };
          img.src = url;
        })
        .catch(err => {
          console.error('Erro ao carregar imagem:', err);
          // Fallback: tentar carregar diretamente
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            setImageElement(img);
          };
          img.src = fullUrl;
          setImageUrl(fullUrl);
        });
    }

    return () => {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [open, photoUrl]);

  const handleRotateLeft = () => {
    setRotation(prev => (prev - 90) % 360);
  };

  const handleRotateRight = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    imgRef.current = e.currentTarget;
  }, []);

  const getCroppedImage = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      try {
        if (!completedCrop || !imageElement || !imgRef.current || !canvasRef.current) {
          console.error('Missing required elements:', {
            completedCrop: !!completedCrop,
            imageElement: !!imageElement,
            imgRef: !!imgRef.current,
            canvasRef: !!canvasRef.current
          });
          resolve(null);
          return;
        }

        // Usar o elemento de imagem carregado, não o ref do DOM
        const image = imageElement;
        const displayedImage = imgRef.current;
        const canvas = canvasRef.current;
        const crop = completedCrop;

        const scaleX = image.naturalWidth / displayedImage.width;
        const scaleY = image.naturalHeight / displayedImage.height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        if (!ctx) {
          resolve(null);
          return;
        }

        const cropWidth = crop.width * scaleX;
        const cropHeight = crop.height * scaleY;

        // Calcular dimensões do canvas considerando a rotação
        if (rotation === 90 || rotation === 270 || rotation === -90 || rotation === -270) {
          canvas.width = cropHeight;
          canvas.height = cropWidth;
        } else {
          canvas.width = cropWidth;
          canvas.height = cropHeight;
        }

        // Limpa o canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.save();

        // Move para o centro do canvas
        ctx.translate(canvas.width / 2, canvas.height / 2);

        // Aplica rotação
        ctx.rotate((rotation * Math.PI) / 180);

        // Desenha a imagem cortada (com offset para centralizar)
        ctx.drawImage(
          image,
          crop.x * scaleX,
          crop.y * scaleY,
          cropWidth,
          cropHeight,
          -cropWidth / 2,
          -cropHeight / 2,
          cropWidth,
          cropHeight
        );

        ctx.restore();

        // Converte canvas para blob
        canvas.toBlob(
          blob => {
            resolve(blob);
          },
          'image/jpeg',
          0.95
        );
      } catch (error) {
        console.error('Erro no getCroppedImage:', error);
        resolve(null);
      }
    });
  }, [completedCrop, rotation, imageElement]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const croppedImage = await getCroppedImage();

      if (!croppedImage) {
        sweetAlert('error', 'Erro', 'Não foi possível processar a imagem');
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('photo', croppedImage, 'photo.jpg');
      formData.append('auxilio_id', auxilioId.toString());

      const response = await axios_instance.post<{ success: boolean; message?: string }>(
        `/edu/auxilio-transporte/update-photo/${auxilioId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        sweetAlert('success', 'Sucesso', 'Foto atualizada com sucesso!', () => {
          onSaveSuccess?.();
          onClose();
        });
      } else {
        sweetAlert('error', 'Erro', response.data.message || 'Erro ao atualizar foto');
      }
    } catch (error) {
      console.error('Erro ao salvar foto:', error);
      sweetAlert('error', 'Erro', 'Erro ao atualizar foto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          Editar Foto
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gridGap={2}>
          <Box display="flex" alignItems="center" gridGap={2}>
            <IconButton onClick={handleRotateLeft} color="primary">
              <RotateLeft />
            </IconButton>
            <Typography variant="body2">Rotação: {rotation}°</Typography>
            <IconButton onClick={handleRotateRight} color="primary">
              <RotateRight />
            </IconButton>
          </Box>

          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{
              minHeight: 400,
              background: '#f5f5f5',
              borderRadius: 4,
              overflow: 'auto',
            }}>
            {imageUrl ? (
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={65 / 75} // Proporção da foto na carteirinha
              >
                <img
                  ref={imgRef}
                  src={imageUrl}
                  alt="Foto para edição"
                  onLoad={onImageLoad}
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    maxWidth: '100%',
                    maxHeight: '500px',
                  }}
                />
              </ReactCrop>
            ) : (
              <Typography>Carregando imagem...</Typography>
            )}
          </Box>

          <Typography variant="caption" color="textSecondary">
            Arraste as bordas para recortar a foto na proporção adequada para a carteirinha.
          </Typography>

          {/* Canvas oculto para processamento */}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          Cancelar
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained" disabled={loading}>
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuxilioPhotoEditor;
